
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { UserProfile } from '../types';
import { Shield, Lock, CheckCircle2, AlertCircle, User, Save, Image as ImageIcon, Sparkles, Eye, EyeOff, Mail, ShieldAlert, Fingerprint } from 'lucide-react';

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [saveReceipts, setSaveReceipts] = useState(false);
  const [emailReports, setEmailReports] = useState(false);
  const [pin, setPin] = useState('');
  const [pdfPin, setPdfPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPdfPin, setShowPdfPin] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
      if (data) {
        setDisplayName(data.display_name || '');
        setSaveReceipts(data.save_receipts || false);
        setEmailReports(data.email_reports_enabled || false);
        setPin(data.pin || '');
        setPdfPin(data.pdf_pin || '');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateSingleSetting = async (field: string, value: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from('user_profiles').update({ [field]: value }).eq('id', user.id);
    } catch (e) {
      console.error("Auto-save failed:", e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase.from('user_profiles').upsert({
        id: user.id,
        display_name: displayName,
        save_receipts: saveReceipts,
        email_reports_enabled: emailReports,
        pin: pin,
        pdf_pin: pdfPin || pin, // Default to app PIN if not set
        updated_at: new Date().toISOString()
      });
      if (error) setMessage({ type: 'error', text: error.message });
      else setMessage({ type: 'success', text: 'Settings updated' });
    } catch (err) { setMessage({ type: 'error', text: "Save failed." }); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center pt-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-8 pb-20">
      <header>
        <h1 className="text-2xl font-black text-slate-900">Settings</h1>
        <p className="text-slate-400 text-sm font-medium">Manage your profile, PIN, and reports</p>
      </header>

      {message && (
        <div className={`p-6 rounded-[2.5rem] flex items-center border animate-in fade-in duration-500 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 shadow-emerald-100/50 shadow-lg' : 'bg-red-50 text-red-700 border-red-100'}`}>
          {message.type === 'success' ? <CheckCircle2 className="mr-3" /> : <AlertCircle className="mr-3" />}
          <span className="text-[10px] font-black uppercase tracking-widest">{message.text}</span>
        </div>
      )}

      <div className="bg-white rounded-[3rem] shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center bg-slate-50/30">
          <div className="w-10 h-10 bg-[#0f172a] rounded-2xl flex items-center justify-center mr-4"><User className="text-amber-400 w-5 h-5" /></div>
          <h2 className="font-black text-slate-900 uppercase tracking-widest text-xs">Profile</h2>
        </div>
        <div className="p-10 space-y-8">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">Your Display Name</label>
            <input type="text" className="w-full px-8 py-5 bg-slate-50 border border-slate-100 rounded-[2rem] font-bold outline-none focus:ring-4 focus:ring-slate-50 transition-all" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[3rem] shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center bg-amber-50/20">
          <div className="w-10 h-10 bg-amber-400 rounded-2xl flex items-center justify-center mr-4"><Sparkles className="text-[#0f172a] w-5 h-5" /></div>
          <h2 className="font-black text-slate-900 uppercase tracking-widest text-xs">Auto Options</h2>
        </div>
        <div className="p-10 space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center"><ImageIcon className="text-slate-600" size={20} /></div>
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">Save Bill Receipts</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Archiving bill scans to storage</p>
              </div>
            </div>
            <button 
              onClick={() => {
                const nextValue = !saveReceipts;
                setSaveReceipts(nextValue);
                updateSingleSetting('save_receipts', nextValue);
              }} 
              className={`w-14 h-8 rounded-full p-1 transition-all ${saveReceipts ? 'bg-[#0f172a]' : 'bg-slate-200'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-sm transform transition-transform ${saveReceipts ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>

          <div className="pt-8 border-t border-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center"><Mail className="text-blue-600" size={20} /></div>
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">Monthly Email Reports</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Get reports by email</p>
              </div>
            </div>
            <button 
              onClick={() => {
                const nextValue = !emailReports;
                setEmailReports(nextValue);
                updateSingleSetting('email_reports_enabled', nextValue);
              }} 
              className={`w-14 h-8 rounded-full p-1 transition-all ${emailReports ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-sm transform transition-transform ${emailReports ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex items-start gap-3 mt-2">
            <ShieldAlert size={16} className="text-blue-600 shrink-0 mt-0.5" />
            <p className="text-[8px] font-bold text-blue-700 uppercase tracking-wider leading-relaxed">
              When turned on, reports are sent on the 1st of every month. Each report is locked with your 4-digit App PIN for your privacy.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[3rem] shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center bg-slate-900">
          <div className="w-10 h-10 bg-slate-800 rounded-2xl flex items-center justify-center mr-4"><Shield className="text-amber-400 w-5 h-5" /></div>
          <h2 className="font-black text-white uppercase tracking-widest text-xs">PIN Settings</h2>
        </div>
        <div className="p-10 space-y-8">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">App PIN</label>
            <div className="relative">
              <input type={showPin ? "text" : "password"} maxLength={4} className="w-full px-8 py-5 bg-slate-50 border border-slate-100 rounded-[2rem] font-black tracking-[0.8em] text-2xl text-slate-900 text-center outline-none" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
              <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-amber-600 transition-colors">{showPin ? <EyeOff size={24} /> : <Eye size={24} />}</button>
            </div>
            <p className="text-center text-[8px] font-black text-slate-300 uppercase tracking-widest mt-4">This 4-digit PIN is used to open the app and your downloaded PDF reports.</p>
          </div>

          <div className="pt-8 border-t border-slate-50">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">PDF Password (Optional)</label>
              <span className="text-[8px] font-black bg-amber-100 text-amber-700 px-2 py-1 rounded-md uppercase">Extra Protection</span>
            </div>
            <div className="relative">
              <input type={showPdfPin ? "text" : "password"} maxLength={6} placeholder="Leave empty to use App PIN" className="w-full px-8 py-5 bg-slate-50 border border-slate-100 rounded-[2rem] font-black tracking-[0.8em] text-2xl text-amber-600 text-center outline-none" value={pdfPin} onChange={(e) => setPdfPin(e.target.value.replace(/\D/g, ''))} />
              <button type="button" onClick={() => setShowPdfPin(!showPdfPin)} className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-amber-600 transition-colors">{showPdfPin ? <EyeOff size={24} /> : <Eye size={24} />}</button>
            </div>
            <p className="text-center text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-4">If you want your PDF reports to use a different password from your app PIN, enter it here.</p>
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="w-full bg-[#0f172a] text-[#fbbf24] py-6 rounded-[2.5rem] font-black shadow-2xl flex items-center justify-center hover:scale-[1.01] active:scale-95 transition-all border border-white/5">
        {saving ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Save size={20} className="mr-3" /> Save Changes</>}
      </button>
    </div>
  );
};

export default Settings;
